import { formatDuration, parseDuration } from "@roxpacer/core";
import { useState, type KeyboardEvent } from "react";

interface DurationInputProps {
  /** Value in seconds. */
  value: number;
  onCommit: (seconds: number) => void;
  label: string;
  /** Text shown after the input, e.g. "/km". */
  suffix?: string;
  allowZero?: boolean;
  max?: number;
  className?: string;
}

/**
 * Text input for m:ss / h:mm:ss durations. Edits are committed on blur or Enter;
 * invalid entries are flagged while typing and reverted on blur.
 */
export function DurationInput({
  value,
  onCommit,
  label,
  suffix,
  allowZero = false,
  max = 24 * 3600,
  className = "",
}: DurationInputProps) {
  const [draft, setDraft] = useState<string | null>(null);
  const parsed = draft === null ? value : parseDuration(draft);
  const valid = parsed !== null && parsed <= max && (allowZero ? parsed >= 0 : parsed > 0);

  const commit = () => {
    if (draft !== null && valid && parsed !== value) onCommit(parsed);
    setDraft(null);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") e.currentTarget.blur();
    if (e.key === "Escape") {
      setDraft(null);
      e.currentTarget.blur();
    }
  };

  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      <input
        type="text"
        inputMode="decimal"
        aria-label={label}
        aria-invalid={!valid}
        title={valid ? undefined : "Use m:ss, e.g. 4:45"}
        className={`field w-[4.75rem] text-right tabular-nums ${valid ? "" : "border-danger! ring-2 ring-danger/30"}`}
        value={draft ?? formatDuration(value)}
        onFocus={(e) => {
          setDraft(formatDuration(value));
          e.currentTarget.select();
        }}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={onKeyDown}
      />
      {suffix && <span className="text-xs text-muted">{suffix}</span>}
    </span>
  );
}
